(() => {
  const campaignRoot = document.querySelector('[data-faf-campaign-root]');
  const plpRoot = document.querySelector('[data-faf-plp-2026]');

  if (!campaignRoot && !plpRoot) return;

  const publish = (name, data = {}) => {
    const eventProperties = {
      ...data,
      faf_schema_version: '1',
      country: window.FAFThemeConfig?.countryCode || '',
      page_type: window.FAFThemeConfig?.pageType || 'storefront',
      page_path: window.location.pathname
    };
    const elevarEvent = `faf_${name}`;

    if (window.FAFAnalytics?.custom) {
      window.FAFAnalytics.custom(elevarEvent, eventProperties);
    } else {
      window.ElevarDataLayer = window.ElevarDataLayer ?? [];
      window.ElevarDataLayer.push({ event: elevarEvent, event_properties: eventProperties });
    }

    const analytics = window.Shopify?.analytics;
    if (!analytics?.publish) return;
    Promise.resolve(analytics.publish(`faf:${name}`, eventProperties)).catch(() => {});
  };

  const urlParams = new URLSearchParams(window.location.search);
  const campaignContext = (extra = {}) => ({
    surface: 'campaign_landing',
    season: campaignRoot?.dataset?.seasonKey || '',
    mode: campaignRoot?.dataset?.campaignMode || extra.mode || 'discover',
    variant: campaignRoot?.dataset?.campaignVariant || extra.handle || 'default',
    match_type: campaignRoot?.dataset?.campaignMatch || extra.matchType || 'default',
    utm_campaign: extra.utmCampaign || urlParams.get('utm_campaign') || '',
    utm_source: extra.utmSource || urlParams.get('utm_source') || '',
    utm_content: extra.utmContent || urlParams.get('utm_content') || ''
  });
  const plpContext = (extra = {}) => ({
    surface: 'plp_2026',
    collection_handle: plpRoot?.dataset?.collectionHandle || '',
    collection_title: plpRoot?.dataset?.collectionTitle || '',
    ...extra
  });

  let campaignMeaningfulTracked = false;
  let plpMeaningfulTracked = false;
  let campaignViewTracked = false;
  let campaignProductListTracked = false;
  let plpProductGridTracked = false;

  const markCampaignMeaningful = (action, extra = {}) => {
    if (campaignMeaningfulTracked) return;
    campaignMeaningfulTracked = true;
    publish('campaign_meaningful_progression', campaignContext({ action, ...extra }));
  };
  const markPlpMeaningful = (action, extra = {}) => {
    if (plpMeaningfulTracked) return;
    plpMeaningfulTracked = true;
    publish('plp_meaningful_progression', plpContext({ action, ...extra }));
  };

  const observeOnce = (node, callback, threshold = 0.25) => {
    if (!node) return;
    if (!('IntersectionObserver' in window)) {
      callback();
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      callback();
    }, { threshold });
    observer.observe(node);
  };

  const productCardContext = (card) => ({
    product_id: card?.dataset?.productId || '',
    product_handle: card?.dataset?.productHandle || ''
  });

  if (campaignRoot) {
    const trackCampaignView = (detail = {}) => {
      if (campaignViewTracked) return;
      campaignViewTracked = true;
      publish('campaign_view', campaignContext(detail));
      observeOnce(campaignRoot.querySelector('[data-campaign-products]'), () => {
        if (campaignProductListTracked) return;
        campaignProductListTracked = true;
        publish('campaign_product_list_view', campaignContext());
      });
    };

    window.addEventListener('faf-campaign-variant', (event) => trackCampaignView(event.detail || {}));
    if (campaignRoot.dataset.campaignReady === 'true') trackCampaignView();

    window.addEventListener('faf-share', (event) => {
      const context = String(event.detail?.context || '');
      if (!campaignRoot.contains(event.target) && !context.startsWith('campaign-landing')) return;
      publish('campaign_share_open', campaignContext({ share_context: context }));
      markCampaignMeaningful('share_open', { share_context: context });
    });

    window.addEventListener('faf-signup-success', (event) => {
      if (!campaignRoot.contains(event.target)) return;
      publish('campaign_signup_success', campaignContext({
        signup_source: event.detail?.source || 'campaign-landing',
        signup_list: event.detail?.list || ''
      }));
      if (window.FAFAnalytics?.subscribe) window.FAFAnalytics.subscribe({ leadType: 'email' });
      markCampaignMeaningful('signup_success');
    });
  }

  if (plpRoot) {
    publish('plp_2026_view', plpContext());
    observeOnce(document.querySelector('#ProductGridContainer'), () => {
      if (plpProductGridTracked) return;
      plpProductGridTracked = true;
      publish('plp_product_grid_viewed', plpContext());
    });
    window.addEventListener('open-filter', () => publish('plp_filter_open', plpContext()));
    window.addEventListener('load-more', () => {
      publish('plp_load_more', plpContext());
      markPlpMeaningful('load_more');
    });
    window.addEventListener('remove-filter', () => {
      publish('plp_filter_change', plpContext({ action: 'remove_filter' }));
      markPlpMeaningful('filter_change', { action: 'remove_filter' });
    });
    window.addEventListener('clear-price-filter', () => {
      publish('plp_filter_change', plpContext({ action: 'clear_price' }));
      markPlpMeaningful('filter_change', { action: 'clear_price' });
    });
  }

  document.addEventListener('change', (event) => {
    if (!plpRoot || !(event.target instanceof HTMLInputElement) || event.target.name !== 'sort_by') return;
    publish('plp_sort_change', plpContext({ sort_by: event.target.value || '' }));
    markPlpMeaningful('sort_change', { sort_by: event.target.value || '' });
  }, true);

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (campaignRoot && campaignRoot.contains(target)) {
      const primary = target.closest('button');
      if (primary?.getAttribute('x-on:click') === 'scrollToProducts()') {
        publish('campaign_primary_cta', campaignContext());
        markCampaignMeaningful('primary_cta');
        return;
      }
      const discovery = target.closest('.faf-campaign-discovery-card');
      if (discovery) {
        const destination = discovery.getAttribute('href') || '';
        publish('campaign_discovery_select', campaignContext({ destination }));
        markCampaignMeaningful('discovery_select', { destination });
        return;
      }
      const productLink = target.closest('[data-elevar-product-card] a[href*="/products/"]');
      if (productLink) {
        const product = productCardContext(productLink.closest('[data-elevar-product-card]'));
        publish('campaign_product_select', campaignContext(product));
        markCampaignMeaningful('product_select', product);
        return;
      }
      const engageLink = target.closest('.faf-campaign-engage a');
      if (engageLink) {
        const destination = engageLink.getAttribute('href') || '';
        publish('campaign_engage_action', campaignContext({ destination }));
        markCampaignMeaningful('engage_action', { destination });
      }
    }

    if (!plpRoot) return;
    const discovery = target.closest('[data-faf-plp-discovery-link]');
    if (discovery && plpRoot.contains(discovery)) {
      const destination = discovery.getAttribute('href') || '';
      const destinationTitle = discovery.dataset.destinationTitle || '';
      publish('plp_discovery_select', plpContext({ destination, destination_title: destinationTitle }));
      markPlpMeaningful('discovery_select', { destination, destination_title: destinationTitle });
      return;
    }
    const quickLink = target.closest('[data-faf-plp-quick-link]');
    if (quickLink && plpRoot.contains(quickLink)) {
      const destination = quickLink.getAttribute('href') || '';
      publish('plp_quick_link_select', plpContext({ destination }));
      markPlpMeaningful('quick_link_select', { destination });
      return;
    }
    const productAnchor = target.closest('[data-faf-plp-product-anchor]');
    if (productAnchor && plpRoot.contains(productAnchor)) {
      publish('plp_view_products', plpContext());
      markPlpMeaningful('view_products');
      return;
    }
    if (target.closest('a[href*="/collections/boot-finder"]')) {
      publish('plp_boot_finder_select', plpContext());
      markPlpMeaningful('boot_finder_select');
      return;
    }
    const productLink = target.closest('[data-elevar-product-card] a[href*="/products/"]');
    if (productLink) {
      const product = productCardContext(productLink.closest('[data-elevar-product-card]'));
      publish('plp_product_select', plpContext(product));
      markPlpMeaningful('product_select', product);
      return;
    }
    const button = target.closest('button');
    const action = button?.getAttribute('x-on:click') || '';
    if (action === 'applyFilters()') {
      publish('plp_filter_apply', plpContext());
      markPlpMeaningful('filter_apply');
    } else if (action === 'clearFilters()') {
      publish('plp_filter_clear', plpContext());
    }
  }, true);

  const initPerformance = () => {
    if (!('PerformanceObserver' in window)) return;
    const supported = PerformanceObserver.supportedEntryTypes || [];
    const observers = [];
    let lcp = 0;
    let cls = 0;
    let longTaskCount = 0;
    let longTaskMs = 0;
    let maxInteractionLatency = 0;
    let sent = false;

    const observe = (type, callback, options = {}) => {
      if (!supported.includes(type)) return;
      try {
        const observer = new PerformanceObserver((list) => callback(list.getEntries()));
        observer.observe({ type, buffered: true, ...options });
        observers.push(observer);
      } catch (_) {}
    };
    observe('largest-contentful-paint', (entries) => {
      const last = entries[entries.length - 1];
      if (last) lcp = Math.max(lcp, last.startTime || 0);
    });
    observe('layout-shift', (entries) => entries.forEach((entry) => {
      if (!entry.hadRecentInput) cls += entry.value || 0;
    }));
    observe('longtask', (entries) => entries.forEach((entry) => {
      longTaskCount += 1;
      longTaskMs += entry.duration || 0;
    }));
    observe('event', (entries) => entries.forEach((entry) => {
      if (entry.interactionId) maxInteractionLatency = Math.max(maxInteractionLatency, entry.duration || 0);
    }), { durationThreshold: 40 });

    const send = () => {
      if (sent) return;
      sent = true;
      observers.forEach((observer) => observer.disconnect());
      const nav = performance.getEntriesByType('navigation')[0];
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      const context = campaignRoot ? campaignContext() : plpContext();
      publish('page_performance', {
        ...context,
        ttfb_ms: nav ? Math.round(Math.max(0, nav.responseStart || 0)) : 0,
        lcp_ms: Math.round(lcp),
        cls: Number(cls.toFixed(4)),
        max_interaction_latency_ms: Math.round(maxInteractionLatency),
        long_task_count: longTaskCount,
        long_task_ms: Math.round(longTaskMs),
        navigation_type: nav?.type || '',
        effective_connection_type: connection?.effectiveType || '',
        save_data: Boolean(connection?.saveData)
      });
    };
    window.addEventListener('pagehide', send, { once: true });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') send();
    });
    window.setTimeout(send, 8000);
  };

  initPerformance();
})();
